// ════════════════════════════════════════════════════════════════════
// CONSENT SIGN PAGE  (PUBLIC, UNAUTHENTICATED)
//
// Rendered when the URL matches /consent/<token>. The client opens
// this on their phone, reads the disclosure, draws a signature on
// the canvas, and submits. The submit handler captures:
//
//   · signature (base64 PNG)
//   · device fingerprint (sha-256 of UA + locale + screen + tz)
//   · geolocation (if granted — optional, never blocks)
//
// and posts to /api/consent-sign. The serverless function adds
// the unspoofable proofs (IP from x-forwarded-for, UA from
// header) and calls the RPC sign_consent which flips the row.
//
// LAYOUT
// ──────────────────────────────────────────────────────────────────
// We follow the canonical CMS Federally-Facilitated Marketplace
// consent form layout (the standard ACA agent/broker permission
// template). Structure top-to-bottom:
//   1. Disclosure body  — exact CMS-required language
//   2. Agency block     — name, NPN, agent name, NPN, phone, email
//   3. Household block  — client name, phone, email, DOB
//   4. Signature canvas
//   5. After submit: Sign Date (long format) + IP Address
//
// Design notes:
// · Mobile-first — most clients open this on their phone in a
//   parking lot.
// · No login / no AuthProvider. Self-contained.
// · The page is the LAST chance to give the client a clean read
//   of what they're signing, so the disclosure text gets the
//   biggest typography weight.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import {
  CONSENT_SCOPE_LABEL, deviceFingerprint, captureGeolocation,
  formatSignDateLong,
} from '../lib/consent';

const HEALTH_PLAN_LABELS = {
  aca_marketplace:    'ACA Marketplace',
  medicare_advantage: 'Medicare Advantage',
  medicare_supplement:'Medicare Supplement',
  medicare_pdp:       'Medicare Part D',
  private_ppo:        'Private PPO',
  short_term:         'Short-Term Medical',
  dental_vision:      'Dental / Vision',
  hospital_indemnity: 'Hospital Indemnity',
  other:              'Other',
};

function SignaturePad({ onChange }) {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const lastPoint = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Match the canvas resolution to the on-screen size for sharp
    // strokes on retina displays.
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = Math.floor(rect.width  * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap   = 'round';
    ctx.lineJoin  = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const ptFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = (e.touches && e.touches[0]) || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPoint.current = ptFromEvent(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = ptFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    if (onChange) onChange('');
  };

  return (
    <div>
      <div
        style={{ touchAction: 'none', userSelect: 'none' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      >
        <canvas
          ref={canvasRef}
          className="block w-full bg-white border-2 border-slate-300"
          style={{ height: 200 }}
        />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-slate-500 italic">Sign with your finger or mouse above</span>
        <button type="button" onClick={clear} className="text-slate-500 hover:text-slate-900 underline">Clear</button>
      </div>
    </div>
  );
}

// Reusable identity block — Agency or Household. Matches the
// reference CMS form's two-column layout.
function IdentityBlock({ title, rows }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
      <div className="border border-slate-300 bg-white p-3 space-y-1.5">
        {rows.filter(r => r.value).map(r => (
          <div key={r.label} className="text-sm text-slate-800">
            <strong>{r.label}:</strong> <span className="break-all">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ConsentSignPage({ token }) {
  const [phase, setPhase]         = React.useState('loading'); // loading | ready | signing | done | declined | error
  const [errorMsg, setErrorMsg]   = React.useState('');
  const [consent, setConsent]     = React.useState(null);
  const [signature, setSignature] = React.useState('');
  const [acked, setAcked]         = React.useState(false);
  const [signResult, setSignResult] = React.useState(null); // { ip, signed_at }

  // Load the consent on mount.
  React.useEffect(() => {
    if (!token) { setPhase('error'); setErrorMsg('Missing consent token.'); return; }
    fetch(`/api/consent-get?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Link is invalid or has expired.');
        }
        return res.json();
      })
      .then((row) => { setConsent(row); setPhase('ready'); })
      .catch((err) => { setErrorMsg(err.message); setPhase('error'); });
  }, [token]);

  async function submit(decline = false) {
    if (!decline && !signature) return;
    if (!decline && !acked) return;
    setPhase('signing');
    try {
      const [fingerprint, geolocation] = await Promise.all([
        deviceFingerprint(),
        decline ? Promise.resolve(null) : captureGeolocation({ timeoutMs: 6000 }),
      ]);
      const res = await fetch('/api/consent-sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signature: decline ? '' : signature,
          fingerprint,
          geolocation,
          decline,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Unable to submit.');
      }
      const body = await res.json().catch(() => ({}));
      setSignResult(body); // { ip, signed_at }
      setPhase(decline ? 'declined' : 'done');
    } catch (err) {
      setErrorMsg(err.message);
      setPhase('error');
    }
  }

  // ── Loading ───────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-slate-500 text-sm">Loading consent form…</div>
      </div>
    );
  }

  // ── Error / Expired ──────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-rose-300 p-6 rounded">
          <div className="text-[0.65rem] font-black uppercase tracking-[0.25em] text-rose-700">Link Invalid</div>
          <h1 className="text-xl font-black text-slate-900 mt-2">This consent link can't be opened.</h1>
          <p className="text-sm text-slate-600 mt-3">{errorMsg || 'The link is invalid, has expired, or has already been signed. If you believe this is a mistake, contact the agent who sent the link.'}</p>
        </div>
      </div>
    );
  }

  // ── Done (signed) — show Sign Date + IP per CMS form layout ──
  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-emerald-400 p-6 rounded text-center">
          <div className="text-[0.65rem] font-black uppercase tracking-[0.25em] text-emerald-700">Consent Recorded</div>
          <h1 className="text-2xl font-black text-slate-900 mt-2">Thank you, {consent.client_name}.</h1>
          <p className="text-sm text-slate-600 mt-3">Your consent has been recorded with the agent. You can close this window. A copy will be on file with your agent.</p>
          <div className="mt-6 text-left bg-slate-50 border border-slate-200 p-3 text-sm">
            <div><strong>Sign Date:</strong> {formatSignDateLong(signResult?.signed_at)}</div>
            {signResult?.ip && <div className="mt-1"><strong>IP Address:</strong> {signResult.ip}</div>}
          </div>
        </div>
      </div>
    );
  }

  // ── Declined ─────────────────────────────────────────────────
  if (phase === 'declined') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-slate-300 p-6 rounded text-center">
          <div className="text-[0.65rem] font-black uppercase tracking-[0.25em] text-slate-700">Declined</div>
          <h1 className="text-2xl font-black text-slate-900 mt-2">Got it.</h1>
          <p className="text-sm text-slate-600 mt-3">We've recorded that you did not consent. Your agent will be notified.</p>
        </div>
      </div>
    );
  }

  // ── Ready / Signing ──────────────────────────────────────────
  const scopeLabel = CONSENT_SCOPE_LABEL[consent.scope] || 'Authorization';
  const planLabels = (consent.plan_types || []).map(t => HEALTH_PLAN_LABELS[t] || t);

  // Format DOB for display (YYYY-MM-DD → MM/DD/YYYY to match CMS style).
  const fmtDob = (iso) => {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-none mx-auto p-4 md:p-8">
        {/* Header chip */}
        <div className="bg-slate-900 border-l-4 border-teal-500 p-5">
          <div className="text-teal-300 text-[0.6rem] font-black uppercase tracking-[0.3em]">{scopeLabel}</div>
          <h1 className="text-white text-xl md:text-2xl font-black uppercase tracking-tight mt-1">Permission to Represent</h1>
          {planLabels.length > 0 && (
            <div className="text-slate-400 text-xs mt-2">Authorized plans: {planLabels.join(' · ')}</div>
          )}
        </div>

        {/* Disclosure body — the canonical CMS-required text */}
        <div className="bg-white border border-slate-200 p-6 mt-3">
          <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
            {consent.consent_text}
          </div>
        </div>

        {/* Two-column identity block, matching the reference CMS form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <IdentityBlock
            title="Company / Agent Representative"
            rows={[
              { label: 'Name of Agency', value: consent.agency_name },
              { label: 'Agency NPN',     value: consent.agency_npn },
              { label: 'Name of Agent',  value: consent.agent_name },
              { label: 'Agent NPN',      value: consent.agent_npn },
              { label: 'Phone Number',   value: consent.agent_phone },
              { label: 'Email Address',  value: consent.agent_email },
            ]}
          />
          <IdentityBlock
            title="Household Contact and/or Authorized Representative"
            rows={[
              { label: 'Full Name',     value: consent.client_name },
              { label: 'Phone Number',  value: consent.client_phone },
              { label: 'Email Address', value: consent.client_email },
              { label: 'Date of Birth', value: fmtDob(consent.client_dob) },
            ]}
          />
        </div>

        {/* Acknowledge checkbox */}
        <label className="flex items-start gap-3 mt-4 bg-white border border-slate-200 p-4">
          <input
            type="checkbox"
            checked={acked}
            onChange={e => setAcked(e.target.checked)}
            className="mt-1 accent-teal-600 h-5 w-5 flex-shrink-0"
          />
          <span className="text-sm text-slate-800">
            I have read, understood, and agree to the authorization above. I confirm I am {consent.client_name || 'the person named above'}.
          </span>
        </label>

        {/* Signature */}
        <div className="bg-white border border-slate-200 p-5 mt-4">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Signature:</h3>
          <SignaturePad onChange={setSignature}/>
        </div>

        {/* Actions */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <button
            onClick={() => submit(false)}
            disabled={!signature || !acked || phase === 'signing'}
            className="bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase tracking-tight text-sm py-3"
          >
            {phase === 'signing' ? 'Submitting…' : 'Sign + Submit'}
          </button>
          <button
            onClick={() => submit(true)}
            disabled={phase === 'signing'}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold uppercase tracking-tight text-sm py-3 px-6"
          >
            I do not consent
          </button>
        </div>

        <div className="text-[0.6rem] text-slate-400 italic mt-6 text-center uppercase tracking-wider">
          Your IP, device, and (if granted) approximate location are recorded with this signature for compliance.
        </div>
      </div>
    </div>
  );
}
